import React, { useState, useEffect } from 'react';

const HonCopierPage = () => {
  const [, setIsVisible] = useState({});
  
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(prev => ({
              ...prev,
              [entry.target.id]: true
            }));
          }
        });
      },
      { threshold: 0.1 }
    );

    document.querySelectorAll('[id^="section-"]').forEach((el) => {
      observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  const FeatureCard = ({ title, description, icon, delay = 0 }) => (
    <div 
      className={`backdrop-blur-md bg-gray-900/30 border border-gray-700/50 rounded-2xl p-8 
                  hover:bg-gray-800/40 transition-all duration-700 transform hover:scale-105
                  hover:border-blue-500/50 group animate-fade-in-up`}
      style={{ animationDelay: `${delay}ms` }}
    >
      <div className="text-4xl mb-6 group-hover:scale-110 transition-transform duration-300">
        {icon}
      </div>
      <h3 className="text-lg sm:text-xl md:text-2xl font-bold text-white mb-4 group-hover:text-blue-400 transition-colors">
        {title}
      </h3>
      <p className="text-gray-300 leading-relaxed">
        {description}
      </p>
    </div>
  );

  const StatCard = ({ number, label, delay = 0 }) => (
    <div 
      className={`text-center animate-fade-in-up`}
      style={{ animationDelay: `${delay}ms` }}
    >
      <div className="text-4xl font-bold text-blue-400 mb-2 counter-animation">
        {number}
      </div>
      <div className="text-gray-300 text-sm uppercase tracking-wider">
        {label}
      </div>
    </div>
  );

  return (
    <div className="min-h-screen text-white relative overflow-hidden">
      {/* Custom CSS */}
      <style jsx>{`
        @keyframes fade-in-up {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-10px); }
        }
        
        @keyframes pulse-glow {
          0%, 100% { box-shadow: 0 0 20px rgba(59, 130, 246, 0.3); }
          50% { box-shadow: 0 0 40px rgba(59, 130, 246, 0.6); }
        }
        
        .animate-fade-in-up {
          animation: fade-in-up 0.8s ease-out forwards;
          opacity: 0;
        }
        
        .animate-float {
          animation: float 3s ease-in-out infinite;
        }
        
        .animate-pulse-glow {
          animation: pulse-glow 2s ease-in-out infinite;
        }
        
        .glass-card {
          backdrop-filter: blur(16px);
          background: rgba(17, 24, 39, 0.3);
          border: 1px solid rgba(255, 255, 255, 0.1);
        }
        
        .gradient-text {
          background: linear-gradient(135deg, #3b82f6, #06b6d4, #8b5cf6);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }
        
        .counter-animation {
          animation: fade-in-up 1s ease-out, pulse-glow 3s ease-in-out infinite 1s;
        }
      `}</style>

      {/* Hero Section */}
      <section className="relative pt-20 pb-32">
        <div className="max-w-7xl mx-auto mt-25 px-6">
          <div className="text-center">
            <div className="animate-fade-in-up">
              <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mb-8">
                <span className="gradient-text font-orbitron">HON-Copier</span>
              </h1>
              <p className="text-xl sm:text-2xl md:text-3xl text-gray-300 mb-4">
                The Ultimate Telegram Signal Copier EA
              </p>
              <p className="text-lg sm:text-xl text-blue-400 mb-12 animate-float">
                Revolutionary Universal Signal Parser with AI Technology
              </p>
            </div>
            
            <div className="animate-fade-in-up" style={{ animationDelay: '400ms' }}>
              <a href='/contact' className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 
                               text-white font-bold py-4 px-12 rounded-full text-lg
                               transform hover:scale-105 transition-all duration-300
                               animate-pulse-glow">
                Get HON-Copier Now
              </a>
            </div>

            {/* Stats Row */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mt-20 max-w-4xl mx-auto">
              <StatCard number="95%" label="Signal Accuracy" delay={600} />
              <StatCard number="1000+" label="Daily Signals" delay={700} />
              <StatCard number="24/7" label="Automated Trading" delay={800} />
              <StatCard number="200+" label="Supported Instruments" delay={900} />
            </div>
          </div>
        </div>
      </section>

      {/* Core Features Section */}
      <section id="section-features" className="py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mb-6 gradient-text">
              Revolutionary Features
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Experience the power of AI-driven signal processing with enterprise-grade automation
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <FeatureCard
              icon="🎯"
              title="Universal Signal Detection"
              description="AI-powered parser that understands ANY signal format - structured, unstructured, multi-line, or single-line. Processes signals in 6+ languages with 95% accuracy."
              delay={200}
            />
            
            <FeatureCard
              icon="📡"
              title="Real-Time Telegram Integration"
              description="Monitor unlimited Telegram channels with 2-second intervals. Advanced filtering for trusted senders and instant message processing."
              delay={400}
            />
            
            <FeatureCard
              icon="🛡️"
              title="Advanced Risk Management"
              description="Multi-layered protection with daily/weekly limits, drawdown protection, and intelligent position sizing. Never risk more than you can afford."
              delay={600}
            />
            
            <FeatureCard
              icon="🧠"
              title="AI Signal Intelligence"
              description="Smart confidence scoring (0-100%), price validation, and automatic symbol mapping for 200+ trading instruments across all markets."
              delay={800}
            />
            
            <FeatureCard
              icon="⚡"
              title="Lightning-Fast Execution"
              description="Sub-second signal processing with optimized memory usage. Designed for 24/7 VPS operation with minimal CPU impact."
              delay={1000}
            />
            
            <FeatureCard
              icon="📈"
              title="Intelligent Position Management"
              description="Automatic breakeven, trailing stops, partial closing, and time-based management. Maximize profits while minimizing risk."
              delay={1200}
            />
          </div>
        </div>
      </section>

      {/* Main Feature Showcase */}
      <section id="section-showcase" className="py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left Column - Features */}
            <div className="space-y-8">
              <div className="animate-fade-in-up">
                <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-6 gradient-text">
                  Why Choose HON-Copier?
                </h2>
                <p className="text-xl text-gray-300 mb-8">
                  Transform your trading with institutional-grade automation that never sleeps
                </p>
              </div>

              <div className="space-y-6">
                {[
                  {
                    icon: "🚀",
                    title: "Never Miss A Signal Again",
                    desc: "24/7 automated monitoring and execution"
                  },
                  {
                    icon: "💰",
                    title: "Maximize Profit Potential", 
                    desc: "Intelligent position management and profit optimization"
                  },
                  {
                    icon: "🎛️",
                    title: "Eliminate Emotional Trading",
                    desc: "Consistent, rule-based execution every time"
                  },
                  {
                    icon: "⚡",
                    title: "Lightning-Fast Processing",
                    desc: "Sub-second signal detection and execution"
                  }
                ].map((item, index) => (
                  <div 
                    key={index}
                    className="flex items-center space-x-4 p-4 rounded-xl glass-card
                               hover:bg-gray-800/50 transition-all duration-300 animate-fade-in-up"
                    style={{ animationDelay: `${index * 200}ms` }}
                  >
                    <div className="text-3xl">{item.icon}</div>
                    <div>
                      <h3 className="text-lg font-semibold text-white">{item.title}</h3>
                      <p className="text-gray-400">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Right Column - Technical Specs */}
            <div className="glass-card rounded-3xl p-8 animate-fade-in-up" style={{ animationDelay: '400ms' }}>
              <h3 className="text-lg sm:text-xl md:text-2xl font-bold mb-6 text-center gradient-text">
                Technical Specifications
              </h3>
              
              <div className="space-y-4">
                {[
                  { label: "Signal Processing Speed", value: "< 1 second" },
                  { label: "Supported Markets", value: "Forex, Crypto, Indices, Commodities" },
                  { label: "Language Support", value: "6+ Languages" },
                  { label: "Risk Management Layers", value: "7 Protection Systems" },
                  { label: "Telegram Channels", value: "Unlimited" },
                  { label: "Execution Accuracy", value: "99.9%" },
                  { label: "Uptime Guarantee", value: "24/7/365" },
                  { label: "Platform Compatibility", value: "MT5, All Brokers" }
                ].map((spec, index) => (
                  <div 
                    key={index}
                    className="flex justify-between items-center py-3 border-b border-gray-700/50
                               hover:border-blue-500/50 transition-colors duration-300"
                  >
                    <span className="text-gray-300">{spec.label}</span>
                    <span className="text-blue-400 font-semibold">{spec.value}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section id="section-cta" className="py-20">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <div className="glass-card rounded-3xl p-12 animate-fade-in-up">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-6 gradient-text">
              Ready to Transform Your Trading?
            </h2>
            <p className="text-xl text-gray-300 mb-8">
              Join thousands of successful traders who have revolutionized their trading with HON-Copier
            </p>
            
            <div className="space-y-4 mb-8">
              <div className="flex items-center justify-center space-x-4 text-lg">
                <span className="text-green-400">✓</span>
                <span>30-Day Money-Back Guarantee</span>
              </div>
              <div className="flex items-center justify-center space-x-4 text-lg">
                <span className="text-green-400">✓</span>
                <span>Lifetime Updates Included</span>
              </div>
              <div className="flex items-center justify-center space-x-4 text-lg">
                <span className="text-green-400">✓</span>
                <span>24/7 Premium Support</span>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href='/contact' className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 
                               text-white font-bold py-4 px-8 rounded-full text-lg
                               transform hover:scale-105 transition-all duration-300
                               animate-pulse-glow">
                Get HON-Copier Now - $230
              </a>
              <a href='https://calendar.app.google/uRXac587SAi2JAeKA' target="_blank" rel="noopener noreferrer" className="border-2 border-blue-500 text-blue-400 hover:bg-blue-500 hover:text-white
                                font-bold py-4 px-8 rounded-full text-lg
                                transform hover:scale-105 transition-all duration-300">
                Book Live Demo (Google Meet)
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HonCopierPage;